import { useState, useCallback } from 'react';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import Toast from '@components/common/Toast';
import { searchTrackData, getTrackDataDownloadUrl } from '@services/api/trackDataService';

/**
 * TrackDataPage — My Track Data
 * Route: /my-track-data
 *
 * Features:
 * - Search by serial number + Clear
 * - Advanced Search (collapsible accordion)
 * - Results tabbed by contact/user name
 * - Table: Name | Date | Download Excel | Download JSON
 *
 * Matches non-prod UI at custdemo.rocktools.my.sandvik/s/my-track-data-page
 */

/** Download icon — reusable for Excel and JSON columns */
function DownloadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="#1441F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <polyline points="9 15 12 18 15 15" />
    </svg>
  );
}

export default function TrackDataPage() {
  const [serialNumber, setSerialNumber] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeContactId, setActiveContactId] = useState(null);
  const [toast, setToast] = useState(null);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await searchTrackData({ serialNumber });
      setResults(data);
      if (data.contacts.length > 0) {
        setActiveContactId(data.contacts[0].id);
      }
    } catch {
      setToast({ type: 'error', message: 'Search failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  const handleClear = () => {
    setSerialNumber('');
    setResults(null);
    setActiveContactId(null);
  };

  const handleDownload = async (fileId, format) => {
    try {
      const url = await getTrackDataDownloadUrl(fileId, format);
      console.log('[TrackDataPage] Downloading:', url);
      setToast({ type: 'success', message: `Downloading ${format.toUpperCase()} file...` });
    } catch {
      setToast({ type: 'error', message: 'Download failed.' });
    }
  };

  const activeContact = results?.contacts.find(c => c.id === activeContactId);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'My track data' }]} />
      <h1 className="font-sandvik-headline font-bold text-2xl text-carbon-black mb-6">
        My track data
      </h1>

      {/* Search bar */}
      <div className="flex gap-3 mb-4">
        <input
          type="text"
          value={serialNumber}
          onChange={e => setSerialNumber(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="Search by serial number"
          className="flex-1 border border-n3 rounded px-4 py-3 text-sm
                     focus:ring-2 focus:ring-electric-blue focus:border-transparent"
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-8 py-3 bg-electric-blue text-white rounded text-sm font-medium
                     hover:bg-blue-dark transition-colors disabled:opacity-50"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
        <button
          onClick={handleClear}
          className="px-8 py-3 border border-n3 rounded text-sm font-medium
                     text-carbon-black hover:bg-n1 transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Advanced Search accordion */}
      <div className="border border-n2 mb-6">
        <button
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium
                     text-carbon-black hover:bg-n1 transition-colors"
        >
          <span>Advanced Search</span>
          <svg
            width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            className={`transition-transform ${advancedOpen ? 'rotate-90' : ''}`}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        {advancedOpen && (
          <div className="px-4 pb-4 border-t border-n2 pt-4">
            <p className="text-xs text-n5">
              {/* TODO: Advanced search filters — serial number range, date range, site name, etc. */}
              Advanced search filters will be available here. Configure serial number ranges,
              date ranges, and site-specific filters.
            </p>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white border border-n2">
          {results.contacts.length === 0 ? (
            <div className="text-center py-10 text-sm text-n5">
              No track data found for this serial number.
            </div>
          ) : (
            <>
              {/* Contact/user tabs */}
              <div className="border-b border-n2 px-4">
                <nav className="flex gap-1" role="tablist">
                  {results.contacts.map(contact => (
                    <button
                      key={contact.id}
                      role="tab"
                      aria-selected={activeContactId === contact.id}
                      onClick={() => setActiveContactId(contact.id)}
                      className={`
                        px-4 py-3 text-sm font-medium transition-colors relative
                        ${activeContactId === contact.id
                          ? 'text-carbon-black bg-n1'
                          : 'text-n5 hover:text-carbon-black'}
                      `}
                    >
                      {contact.name}
                      {activeContactId === contact.id && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue" />
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Data table */}
              {activeContact && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-n2">
                        <th className="text-left text-xs font-semibold text-carbon-black px-4 py-3">
                          Name
                        </th>
                        <th className="text-left text-xs font-semibold text-carbon-black px-4 py-3">
                          Date
                        </th>
                        <th className="text-center text-xs font-semibold text-carbon-black px-4 py-3">
                          Download excel
                        </th>
                        <th className="text-center text-xs font-semibold text-carbon-black px-4 py-3">
                          Download JSON
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeContact.files.map((file, idx) => (
                        <tr
                          key={file.id}
                          className={`border-b border-n2 hover:bg-n1 transition-colors
                                      ${idx % 2 === 1 ? 'bg-n1/50' : ''}`}
                        >
                          <td className="px-4 py-3 text-sm text-carbon-black">
                            {file.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-n6">
                            {file.date}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDownload(file.id, 'excel')}
                              className="inline-flex items-center justify-center p-1
                                         hover:bg-blue-light/30 rounded transition-colors"
                              title="Download Excel"
                            >
                              <DownloadIcon />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDownload(file.id, 'json')}
                              className="inline-flex items-center justify-center p-1
                                         hover:bg-blue-light/30 rounded transition-colors"
                              title="Download JSON"
                            >
                              <DownloadIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
